import { App, nextTick } from 'vue';

/**
 * Global Form Validation Scroll Plugin
 * 
 * This plugin automatically scrolls to the first error field when form validation fails.
 * It works with all Quasar q-form components in the application without any manual setup.
 * 
 * Bu plugin, form validasyonu başarısız olduğunda otomatik olarak ilk hatalı alana scroll yapar.
 * Uygulamadaki tüm Quasar q-form bileşenleriyle otomatik olarak çalışır, manuel kurulum gerektirmez.
 * 
 * How it works / Nasıl çalışır:
 * 1. Monitors DOM for any form submission attempts
 * 2. Checks for validation errors after submission
 * 3. Automatically scrolls to first error field
 */

const scrollToFirstError = async () => {
  await nextTick();
  
  // Small delay to ensure error classes are applied
  setTimeout(() => {
    // Find the first element with error
    const firstErrorElement = document.querySelector(
      '.q-field--error, .q-input--error, .q-select--error'
    ) as HTMLElement;

    if (firstErrorElement) {
      // Scroll to the error element with smooth animation
      firstErrorElement.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });

      // Focus on the input if possible
      const inputElement = firstErrorElement.querySelector(
        'input, textarea, .q-select'
      ) as HTMLElement;

      if (inputElement && typeof inputElement.focus === 'function') {
        setTimeout(() => {
          inputElement.focus();
        }, 300); // Wait for scroll animation to complete
      }
    }
  }, 100);
};

export default {
  install(app: App) {
    // Monitor form submissions globally
    if (typeof document !== 'undefined') {
      // Use capture phase to catch submit events before they bubble
      document.addEventListener('submit', (event) => {
        // Check if any validation errors exist after a short delay
        setTimeout(() => {
          const hasErrors = document.querySelector('.q-field--error');
          if (hasErrors) {
            scrollToFirstError();
          }
        }, 50);
      }, true);

      // Also monitor for validation errors that might occur without submit
      // (e.g., when clicking a button that triggers manual validation)
      const observer = new MutationObserver((mutations) => {
        for (const mutation of mutations) {
          if (mutation.type === 'attributes' && 
              mutation.attributeName === 'class') {
            const target = mutation.target as HTMLElement;
            if (target.classList.contains('q-field--error')) {
              // Only scroll if this is part of a recent validation attempt
              const recentValidation = (window as any).__vaultn_validation_timestamp;
              if (recentValidation && Date.now() - recentValidation < 1000) {
                scrollToFirstError();
                (window as any).__vaultn_validation_timestamp = 0;
              }
            }
          }
        }
      });

      // Observe the entire document for class changes
      requestAnimationFrame(() => {
        observer.observe(document.body, {
          attributes: true,
          attributeFilter: ['class'],
          subtree: true,
        });
      });

      // Track validation attempts
      document.addEventListener('click', (event) => {
        const target = event.target as HTMLElement;
        // Check if clicked element is a submit button or triggers form validation
        if (target.tagName === 'BUTTON') {
          const buttonElement = target as HTMLButtonElement;
          if (buttonElement.type === 'submit' || target.closest('form')) {
            (window as any).__vaultn_validation_timestamp = Date.now();
          }
        }
      }, true);
    }

    console.log('[VaultN] Form validation scroll plugin initialized - all forms will auto-scroll to errors');
  },
};
